import * as moment from 'moment';
import {
    COMMON_FORM_STAGE,
    CREATE_FORM_STAGE,
    DATE_FORMAT,
    FLOW_STATUS,
    FORM_TYPE,
    HISTORY_STATUS,
    identityOptions,
    STATUS,
    SUB_DIR,
    UPDATE_FORM_STAGE,
    URL,
    VERIFY_FORM_STAGE,
    DAY_NUMBER
} from '../Config/appConfig';
import {Company} from '../Config/dataModel';
import { getLanguage } from './language';

const getParties = (data: Company[]) => {
    const parties = {};
    data.forEach( d => {
        parties[d.shortId] = {
            x500Name: d.x500Name,
            company: d.displayName
        };
    });
    return parties;
};

const getDate = (date: string) => {
    return moment(date, DATE_FORMAT.INSTANT);
};

const getFormattedDate = (date: string) => {
    const formattedDate = getDate(date);
    return formattedDate.format(DATE_FORMAT.DATE);
};

const getFormattedDatetime = (date: string) => {
    const formattedDate = getDate(date);
    return formattedDate.format(DATE_FORMAT.DATETIME);
};

const getMaintenanceDatetime = (date: moment.Moment) => {
    if (getLanguage() === "zh-hk") {
        let formattedDate = date.format(DATE_FORMAT.MAINTENANCE_CHI);
        const splitFrom = '星期';
        const splitDate = formattedDate.split(splitFrom);
        const splitDay = splitDate[1].slice(0, 1);
        const splitTime = splitDate[1].slice(1, splitDate[1].length);
        return `${splitDate[0]} ${splitFrom}${DAY_NUMBER[splitDay]}${splitTime}`;
    }
    else return date.format(DATE_FORMAT.MAINTENANCE);
};

const getLicense = (license: string) => {
    return license === '' ? 'N/A' : license;
};

const getDuration = (start: moment.Moment, end: moment.Moment) => {
    return moment.duration(end.startOf('day').diff(start.startOf('day'))).asDays() + 1;
};

const getFlowStatus = (short: boolean, status: string, enquiry: boolean) => {
    switch (status) {
        case FLOW_STATUS.CREATED:
            return enquiry ? STATUS.VALID : (short ? STATUS.CREATED : STATUS.SUBAGENT_CREATED);
        case FLOW_STATUS.VOIDED:
            return STATUS.VOIDED;
        default:
            return enquiry ? STATUS.VALID : STATUS.CONFIRMED;
    }
};

const getHistoryStatus = (confirmed: { created: string }, issued: { created: string }, voided: { created: string }, createDate: string, lastModifiedDate: string) => {
    if (lastModifiedDate === createDate) {
        return HISTORY_STATUS.CREATED;
    }
    else if (confirmed && lastModifiedDate === confirmed.created) {
        return HISTORY_STATUS.CONFIRMED;
    }
    else if (issued && lastModifiedDate === issued.created) {
        return HISTORY_STATUS.ISSUED;
    }
    else if (voided && lastModifiedDate === voided.created) {
        return HISTORY_STATUS.VOIDED;
    }
    return HISTORY_STATUS.UPDATED;
};

const getStatus = (end: string, status: string, enquiry: boolean) => {
    if (status === FLOW_STATUS.VOIDED || getDuration(moment(), moment(end)) > 0) {
        return getFlowStatus(false, status, enquiry);
    }
    return STATUS.EXPIRED;
};

const getPolicyType = (policyType: string) => {
    const type = policyType.split(/(?=[A-Z])/);
    return `${type[0].charAt(0).toUpperCase()}${type[0].slice(1)}${type[1] ? ' ' + type[1] : ''}`;
};

const getIdentityNo = (identity: string, identityNo: string, checksum: string) => {
    let result = identityNo;
    if (identity === identityOptions[0].value) {
        result += checksum;
    }
    return result;
};

const isHkidValid = (hkid: string) => {
    const identityNo = hkid.split('');
    if (!((identityNo.length === 7 || identityNo.length === 8) && identityNo[0].match(/[a-z]/i))) {
        return false;
    }
    let idx = 1;
    if (identityNo.length === 8) {
        if (identityNo[1].match(/[a-z]/i)) {
            idx = 2;
        }
        else {
            return false;
        }
    }
    return (!!hkid.substring(idx).match(/^\d+$/));
};

const isHkidChecksumValid = (hkid: string, checksum: string) => {
    if (!isHkidValid(hkid)) {
        return false;
    }
    hkid = hkid.toUpperCase();
    let sum = 324;
    let identityNo = hkid.split('');
    if (identityNo.length === 8) {
        return true;
    }
    for (let i = 0; i < identityNo.length; i++) {
        let num = 0;
        if (identityNo[i].match(/[A-Z]/)) {
            num = hkid.charCodeAt(i) - 55;
        }
        else {
            num = parseInt(identityNo[i], 10);
        }
        sum += num * (identityNo.length - i + 1);
    }
    const remainder = sum % 11;
    let correctChecksum = '0';
    if (remainder) {
        correctChecksum = (11 - remainder).toString(16).toUpperCase();
    }
    return checksum === correctChecksum;
};

const isIdentityNoValid = (identity: string, identityNo: string, checksum: string) => {
    if (identity === identityOptions[0].value) {
        return isHkidChecksumValid(identityNo, checksum);
    }
    return identity.length > 0;
};

const getCompanyName = (subAgentCode: string, insurerCode: string, data: any, isInsurer: boolean) => {
    if (subAgentCode === insurerCode || Object.keys(data).length === 0) {
        return 'N/A';
    }
    const code = isInsurer ? subAgentCode : insurerCode;
    const companyDetail = data[code];
    return companyDetail ? companyDetail.company : `[Withdrawn ${isInsurer ? 'Sub-agent' : 'Insurer'}] ${code}`;
};

const getSubagentName = (subAgentCode: string, insurerCode: string, data: any, isInsurer: boolean) => {
    if (subAgentCode === insurerCode) {
        return '';
    }
    const counterpartyDetail = data.allCounterparties[subAgentCode];
    return isInsurer ? (counterpartyDetail ? counterpartyDetail.company : `[Withdrawn Sub-agent] ${subAgentCode}`) : data.user.company;
};

const getHistoryCompanyName = (code: string, data: any, requestedBy: string) => {
    return requestedBy === data.user.userRole ? data.user.company : data.allCounterparties[code].company;
};

const getCompanyNameFromList = (data: Company[], code: string) => {
    let company = data.find((company) => code === company.shortId)
    if (getLanguage() === "en")
        return company.displayName;
    else
        return company.displayNameChi;
};

const getCompany = (value: string, label: string) => {
    return {
        value,
        label
    };
};

const getInsurerId = (data: string) => {
    return {insurerId: data};
};

const getInsurerOptions = (data: object | Company[]) => {
    if (Array.isArray(data)) {
        if (getLanguage() === "en")
            return data.map((company) => getCompany(company.x500Name, company.displayName));
        else
            return data.map((company) => getCompany(company.x500Name, company.displayNameChi));
    }
    const options = [];
    for (const item in data) {
        options.push(getCompany(data[item].x500Name, data[item].company));
    }
    return options;
};

const getTitle = (formType: string, stage: string) => {
    let title = '';
    let rowTitle1 = 'Cover Note Number';
    switch (formType) {
        case FORM_TYPE.IN_VIEW_COVER_NOTE:
        case FORM_TYPE.UPDATE_COVER_NOTE:
            title = getStageTitle(stage, 'Cover Note');
            break;
        case FORM_TYPE.SA_VIEW_POLICY:
        case FORM_TYPE.UPDATE_POLICY:
            title = getStageTitle(stage, 'Policy');
            rowTitle1 = 'Policy Number';
            break;
        case FORM_TYPE.SA_CREATE_COVER_NOTE:
            title = getStageTitle(stage, 'Cover Note');
            break;
        case FORM_TYPE.IN_CREATE_COVER_NOTE:
            title = getStageTitle(stage, 'Record');
            break;
        case FORM_TYPE.IN_CREATE_POLICY:
            title = getStageTitle(stage, 'Record');
            rowTitle1 = 'Policy Number';
            break;
        case FORM_TYPE.IN_CONVERT_TO_POLICY:
            title = getStageTitle(stage, 'Policy');
            rowTitle1 = 'Policy Number';
            break;
        default:
            break;
    }
    return {title, rowTitle1};
};

const getStageTitle = (stage: string, type: string) => {
    switch (stage) {
        case CREATE_FORM_STAGE.CREATE:
            return `Create ${type}`;
        case CREATE_FORM_STAGE.CONFIRM_CREATE:
            return `${type} Summary`;
        case CREATE_FORM_STAGE.CREATE_CN_SUMMARY:
        case CREATE_FORM_STAGE.CREATE_PY_SUMMARY:
            return `${type} Successfully Submitted`;
        case VERIFY_FORM_STAGE.CONVERT_TO_POLICY:
            return 'Create Policy';
        case COMMON_FORM_STAGE.PROCEED:
        case VERIFY_FORM_STAGE.ISSUE_POLICY:
        case COMMON_FORM_STAGE.PRINT:
            return `View ${type}`;
        case VERIFY_FORM_STAGE.APPROVE_CN:
            return 'Verify Cover Note';
        case UPDATE_FORM_STAGE.UPDATE:
            return `Update ${type}`;
        case UPDATE_FORM_STAGE.CONFIRM_UPDATE:
            return `${type} Update Summary`;
        case UPDATE_FORM_STAGE.UPDATE_SUMMARY:
            return `${type} Successfully Submitted`;
        case UPDATE_FORM_STAGE.CONFIRM_VOID:
            return `Void ${type}`;
        default:
            return '';
    }
};

const getDest = (path: string) => {
    switch (path.toLowerCase()) {
        case SUB_DIR.POLICY:
            return URL.POLICY;
        case SUB_DIR.ARCHIVED:
        case STATUS.EXPIRED:
            return URL.ARCHIVED;
        case SUB_DIR.COMPANIES:
        case SUB_DIR.NORMAL_USERS:
        case SUB_DIR.API_USERS:
            return URL.DASHBOARD;
        default:
            return URL.COVER_NOTE;
    }
};

const getCsvIdentity = (identity: string, identityNo: string) => {
    let type = '';
    for (let i = 0; i < identityOptions.length; i++) {
        if (identity === identityOptions[i].value) {
            type = identityOptions[i].label;
        }
    }
    if (identity === identityOptions[0].value) {
        return `${type}      ${identityNo.slice(0, -1)}(${identityNo.slice(identityNo.length - 1, identityNo.length)})`;
    }
    return `${type}      ${identityNo}`;
};

const isPasswordStrong = (password: string) => {
    let checkList = 0;
    if (password.length < 10) {
        return false;
    }
    if (/.*[a-z].*/.test(password)) {
        checkList += 1;
    }
    if (/.*[A-Z].*/.test(password)) {
        checkList += 1;
    }
    if (/.*\d.*/.test(password)) {
        checkList += 1;
    }
    if (/[-!$%^&*()_+|~=`{}\[\]:";'<>?,.\/#]/.test(password)) {
        checkList += 1;
    }
    if (checkList >= 3) {
        return true;
    }
    else {
        return false;
    }
};

const isSameContentType = (path: string, type: string, end: string, status: string) => {
    if (getStatus(end, status, false) === STATUS.EXPIRED) return path === SUB_DIR.ARCHIVED;
    else return path.replace('-', '') === type.toLowerCase();
};

const isAlphanumeric = (data: string) => {
    return data.match(/^([a-z0-9]|)+$/i);
};

const isValidEmail = (data: string) => {
    return data.match(/.+\@.+\..+/);
};

const isValidChassisNumber = (data: string) => {
    return data.length <= 25;
};

const util = {
    getParties,
    getDate,
    getFormattedDate,
    getFormattedDatetime,
    getMaintenanceDatetime,
    getLicense,
    getDuration,
    getFlowStatus,
    getHistoryStatus,
    getStatus,
    getPolicyType,
    getIdentityNo,
    isHkidChecksumValid,
    isIdentityNoValid,
    isValidChassisNumber,
    getCompanyName,
    getSubagentName,
    getHistoryCompanyName,
    getCompanyNameFromList,
    getCompany,
    getInsurerId,
    getInsurerOptions,
    getTitle,
    getDest,
    getCsvIdentity,
    isPasswordStrong,
    isSameContentType,
    isAlphanumeric,
    isValidEmail
};

export default util;



// WEBPACK FOOTER //
// ./src/Util/util.ts